import { useState } from 'react';
import { Button, TextArea } from 'common/components';
import { useFormContext } from 'react-hook-form';
import { WorkflowConfigFormData } from 'pages/WorkflowBuilder/AgenticWorkflowBuilder';
import { usePostTestGenericQualification } from 'services/hooks/mutations';
import {
    TestGenericQualificationConfig,
    TestGenericQualificationResponse,
} from 'pages/WorkflowBuilder/WorkflowBuilder.types';
import { formatGenericTag } from 'pages/WorkflowBuilder/workflowNodeUtils';
import { toast } from 'react-toastify';
import { QualificationBadges } from 'components/AgentCallConsole/components/QualificationBadges/QualificationBadges';
import { formatQualifications } from 'utils/livekitContext.utils';
import LoadingSpinner from 'components/LoadingSpinner';

export const GenericPromptTestTab = () => {
    const [testResults, setTestResults] = useState<TestGenericQualificationResponse | null>(null);
    const {
        getValues,
        register,
        trigger,
        formState: { errors: formErrors },
    } = useFormContext<WorkflowConfigFormData>();
    const { postTestGenericQualification, isPending } = usePostTestGenericQualification();

    const handleTest = async () => {
        const [callTranscript, genericTags, genericPrompts] = getValues([
            'callTestTranscript',
            'genericTags',
            'genericPrompts',
        ]);

        await trigger(['callTestTranscript', 'genericTags', 'genericPrompts']);

        if (Object.keys(formErrors).length > 0 || !callTranscript) {
            toast.error('Please fill all the fields before testing');
            return;
        }

        // validate object structure of callTranscript
        try {
            const callTranscriptObject = JSON.parse(callTranscript);
            if (!callTranscriptObject.speeches) {
                return toast.error(
                    'Call transcript must be an object with key "speeches" and value as an array of speech objects with key "text" and "speaker"'
                );
            }
        } catch (error) {
            return toast.error(
                'Call transcript must be a valid JSON object with key "speeches" and value as an array of speech objects with key "text" and "speaker"'
            );
        }

        const sortedTags = [...genericTags].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const config: TestGenericQualificationConfig = {
            generic_tags: Object.fromEntries(
                sortedTags.map((tag, index) => {
                    const formattedTag = formatGenericTag(tag.value);
                    return [
                        formattedTag,
                        {
                            type: tag.type,
                            prompt: genericPrompts[formattedTag]?.value ?? '',
                            order: tag.order ?? index,
                        },
                    ];
                })
            ),
            generic_prompts: {
                general_prompt: genericPrompts.general_prompt.value,
                sanity_check_prompt: genericPrompts.sanity_check_prompt?.value ?? '',
                unqualify_prompt: genericPrompts.unqualify_prompt.value,
                qualify_prompt: genericPrompts.qualify_prompt.value,
                undetermined_prompt: genericPrompts.undetermined_prompt.value,
            },
        };

        postTestGenericQualification(
            {
                transcript: callTranscript,
                config,
            },
            {
                onSuccess: (data) => {
                    setTestResults(data);
                },
                onError: () => {
                    setTestResults(null);
                },
            }
        );
    };

    return (
        <div className='flex flex-col gap-lg pt-xl'>
            <p className='text-gray-700'>
                Test your prompts by entering a call transcript and see how the AI evaluates the tags.
            </p>
            <TextArea
                label='Call Transcript'
                rows={10}
                helperText='Provide call transcript as an object with key "speeches" and value as an array of speech objects with key "text" and "speaker".'
                error={!!formErrors.callTestTranscript}
                errorText={formErrors.callTestTranscript?.message}
                {...register('callTestTranscript', { required: 'Call transcript is required' })}
            />
            {isPending && (
                <div className='flex justify-center items-center h-full'>
                    <LoadingSpinner text='Validating prompts with provided transcript...' />
                </div>
            )}
            {testResults && !isPending && (
                <>
                    <div>
                        <h3>Extracted results</h3>
                        <div className='flex flex-col gap-xs mt-md'>
                            <p>
                                Qualified: <strong>{testResults.qualification.qualified.toString()}</strong>
                            </p>
                            <p>
                                Undetermined: <strong>{testResults.qualification.undetermined.toString()}</strong>
                            </p>
                            <p>
                                Unqualified: <strong>{testResults.qualification.unqualified.toString()}</strong>
                            </p>
                        </div>
                    </div>
                    <QualificationBadges qualifications={formatQualifications(testResults.extraction.tags, false)} />
                </>
            )}
            <Button variant='secondary' onClick={handleTest} isLoading={isPending}>
                Test
            </Button>
        </div>
    );
};
